import type { AuthConfig } from '../types';
import { getAuthConfig } from './apiClient';
import { setTokenCacheKey } from '../utils/authStorage';

/**
 * Runtime configuration, fetched once from `GET /api/auth-config`.
 *
 * This module is what replaces `import envConfig from "../environment-property/property.json"`
 * — an import that appeared in seven files in the old app and, being a static JSON
 * import, was inlined by webpack into `main.*.js` along with both APIM subscription
 * keys. Nothing here can carry a secret: the endpoint only ever returns the public keys
 * (see backend/src/routes/authConfig.ts, whose test pins the exact key set).
 *
 * It also removes the per-environment Docker image. The old CI renamed
 * `property-$(EnvironmentConfig).json` over `property.json` before building
 * (azure-pipeline.yml:35-42), so the environment was baked into the bundle; now one
 * build asks the server it was served from.
 *
 * Held in a module-level variable rather than context so non-component code (okta.ts,
 * the token resolver) can read it without prop-drilling — the same access shape
 * `envConfig` had.
 */

let config: AuthConfig | null = null;
let inFlight: Promise<AuthConfig> | null = null;

/**
 * Fetches the config, or returns the in-flight promise if a fetch is already running.
 *
 * The de-duplication is load-bearing under StrictMode, which invokes effects twice in
 * development: without it, two requests race and the second `setTokenCacheKey` could land
 * after a token had already been written under the first key.
 */
export function loadRuntimeConfig(): Promise<AuthConfig> {
  if (config) return Promise.resolve(config);
  if (inFlight) return inFlight;

  inFlight = getAuthConfig()
    .then((loaded) => {
      config = loaded;
      // Must happen before any token is read or written — every authStorage call keys
      // off it (old app: authConfiguration.tokenCacheKey).
      setTokenCacheKey(loaded.tokenCacheKey);
      return loaded;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * The loaded config, or null before the fetch resolves.
 *
 * Prefer the `useAuth()` context inside components; this exists for the handful of
 * non-component call sites that read config the way `envConfig` used to be read.
 */
export function getRuntimeConfig(): AuthConfig | null {
  return config;
}

/** Test seam. */
export function __setRuntimeConfigForTest(next: AuthConfig | null): void {
  config = next;
  inFlight = null;
  if (next) setTokenCacheKey(next.tokenCacheKey);
}
